import React, { useCallback, useMemo, type KeyboardEvent, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { Provider as SmartCardProvider } from '@atlaskit/smart-card';
import { ff } from '@atlassian/jira-feature-flagging';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id';
/**
 * Components required to compose the extensible cross product search dialog
 */
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller';
import {
	BitbucketTab,
	CompassTab,
	ConfluenceFilterContextProvider,
	ConfluenceTab,
	ExtensibleJiraTab,
	JiraFilterContextProvider,
	MultiProductDialog,
	OpsgenieTab,
	TownsquareTab,
	TrelloTab,
	UserInputContextProvider,
} from '@atlassian/product-search-dialog';
import type { LinkComponentProps } from '@atlassian/search-dialog';
import type { ExtensibleDialogProps } from './types';

const TOWNSQUARE_SMARTS_PRODUCT_IDENTIFIER = 'watermelon';
const COMPASS_PRODUCT_IDENTIFIER = 'compass';
const COLLABORATION_GRAPH_URL = '/gateway/api/collaboration';
const ANALYTICS_CONTEXT_LOCATION = 'extensibleJiraTab';
export const JIRA_SCOPES = ['jira.issue', 'jira.project,board,plan,filter', 'urs.user'];

const jiraLinkComponent =
	(onJiraNavigation: (href: string, event: KeyboardEvent | MouseEvent) => void) =>
	(linkProps: LinkComponentProps) => {
		const { href } = linkProps;
		const onClick = (event: KeyboardEvent | MouseEvent) => {
			if (!(event.ctrlKey || event.metaKey)) {
				onJiraNavigation(href || '', event);
			}
		};

		return (
			<LinkComponent onClick={onClick} role="none" {...linkProps}>
				{linkProps.children}
			</LinkComponent>
		);
	};

const PreviewWrapper = ({ children }: { children: React.ReactElement }) => (
	<AnalyticsContext
		data={{
			attributes: { location: ANALYTICS_CONTEXT_LOCATION },
		}}
	>
		<SmartCardProvider>{children}</SmartCardProvider>
	</AnalyticsContext>
);

export const ExtensibleCrossProductSearchDialog = (props: ExtensibleDialogProps) => {
	const {
		cloudId,
		aggregatorUrl,
		confluenceFeatures,
		formatDate,
		isExpanded,
		forwardRef,
		jiraFeatures,
		onNavigate,
		onOpen,
		onClose,
		theme,
		pageLoadMetrics,
		dialogFeatures,
	} = props;

	const secondaryProductNavigate = (href: string) => {
		window.location.assign(href);
	};
	const onJiraNavigation = useCallback(
		(href: string, event: KeyboardEvent | MouseEvent) => {
			onNavigate(href, event);
			onClose();
		},
		[onClose, onNavigate],
	);
	const onNavigateMultiProduct = (id: string, href: string, event: KeyboardEvent | MouseEvent) => {
		switch (id) {
			case 'jira': {
				onJiraNavigation(href, event);
				break;
			}
			default:
				secondaryProductNavigate(href);
		}
	};
	const JiraLinkComponent = useMemo(() => jiraLinkComponent(onJiraNavigation), [onJiraNavigation]);
	const { data: orgId } = useOrgId();
	const projectData = useProject();

	const { user } = useCurrentUser().data;
	const userDetails = useMemo(
		() =>
			user && !user.isAnonymous && user.accountId
				? {
						id: user.accountId,
						name: user.userFullname,
						// @ts-expect-error - TS2339 - Property 'avatarUrls' does not exist on type 'DataBasic | (DataBasic & ResponseSuccess)'. | TS2339 - Property 'avatarUrls' does not exist on type 'DataBasic | (DataBasic & ResponseSuccess)'.
						avatarUrl: user.avatarUrls ? user.avatarUrls['48x48'] : '',
					}
				: undefined,
		[user],
	);

	const licensedProducts = useLicensedProducts();

	return (
		<UserInputContextProvider stickySearchEnabled isExpanded={isExpanded}>
			<ConfluenceFilterContextProvider isEnabled={isExpanded}>
				<JiraFilterContextProvider isEnabled={isExpanded}>
					<MultiProductDialog
						aggregatorUrl={aggregatorUrl}
						abTestCloudId={cloudId}
						isExpanded={isExpanded}
						setIsExpanded={(value: boolean) => (value ? onOpen() : onClose())}
						theme={theme}
						// @ts-expect-error - TS2322 - Type 'HTMLInputElementRef' is not assignable to type 'Ref<HTMLInputElement>'.
						forwardRef={forwardRef}
						// @ts-expect-error - TS2322 - Type '(id: string, href: string, event: KeyboardEvent | MouseEvent) => void' is not assignable to type '(productId: string, href: string, event: MouseEvent<Element, MouseEvent> | KeyboardEvent) => void'.
						onNavigateGeneric={onNavigateMultiProduct}
						pageLoadMetrics={pageLoadMetrics}
						dialogFeatures={dialogFeatures}
					>
						{({ onRetry }) => (
							<>
								<PreviewWrapper>
									<ExtensibleJiraTab
										onRetry={onRetry}
										permissionSupplier={() => Promise.resolve(JIRA_SCOPES)}
										features={jiraFeatures}
										order={0}
										isPrefetchingEnabled={!ff('jira.frontend.quick-search.disable-pre-query-fetch')}
										linkComponent={JiraLinkComponent}
										config={{
											cloudId,
											collaborationGraphUrl: COLLABORATION_GRAPH_URL,
										}}
										currentProject={
											projectData.data
												? {
														id: projectData.data.id.toString(),
														label: projectData.data.name,
														avatarUrl: projectData.data.avatarUrl,
													}
												: undefined
										}
										currentUser={userDetails}
										autocorrectEnabled
									/>
								</PreviewWrapper>
								<ConfluenceTab
									isExpanded={isExpanded}
									formatDate={formatDate}
									onRetry={onRetry}
									features={confluenceFeatures}
									order={1}
								/>
								<BitbucketTab
									order={2}
									onRetry={onRetry}
									features={{
										enableSingleScopesCall: true,
									}}
								/>
								<CompassTab
									crossFlowEnabled
									sourceContext="jira"
									isCompassActivated={licensedProducts?.compass ?? false}
									isUserAnonymous={user.isAnonymous}
									order={3}
									onRetry={onRetry}
									userTeamConfig={{
										context: {
											contextType: 'filter',
											// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
											organizationId: orgId!,
											principalId: 'Context',
											productKey: COMPASS_PRODUCT_IDENTIFIER,
											siteId: cloudId,
										},
									}}
								/>
								<OpsgenieTab order={4} onRetry={onRetry} cloudId={cloudId} />
								<TownsquareTab
									order={5}
									onRetry={onRetry}
									userTeamConfig={{
										context: {
											contextType: 'filter',
											// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
											organizationId: orgId!,
											principalId: 'Context',
											productKey: TOWNSQUARE_SMARTS_PRODUCT_IDENTIFIER,
											siteId: cloudId,
										},
									}}
									// @ts-expect-error - TS2322 - Type '{ order: number; onRetry: () => void; userTeamConfig: { context: { contextType: string; principalId: string; productKey: string; siteId: string; }; }; cloudId: string; }' is not assignable to type 'IntrinsicAttributes & ExtensibleTabProps & { userTeamConfig?: UserTeamConfig | undefined; } & { children?: ReactNode; }'.
									cloudId={cloudId}
								/>
								<TrelloTab order={5} onRetry={onRetry} />
							</>
						)}
					</MultiProductDialog>
				</JiraFilterContextProvider>
			</ConfluenceFilterContextProvider>
		</UserInputContextProvider>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkComponent = styled.a({
	'&:active, &:hover': {
		outline: 'none',
	},
});
